import React, { useState } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { InformationCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectDbtExecutionMode,
  setDbtExecutionMode,
  selectIsSnowflakeAccountValidatedForDbt,
  setIsSnowflakeAccountValidatedForDbt,
} from '../reducers/dbtMigrationSlice';
import { selectProjectPath } from 'app/sharedSlice';
import { selectSnowflakeAccount } from 'app/snowflakeSlice';
import { useValidateSnowflakeDbtPermissionsMutation } from 'app/createApi';
import StepWrapper from 'components/StepWrapper';

export interface ExecutionModeStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function ExecutionModeStep(props: ExecutionModeStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const dbtExecutionMode = useAppSelector(selectDbtExecutionMode);
  const isSnowflakeAccountValidatedForDbt = useAppSelector(selectIsSnowflakeAccountValidatedForDbt);

  const projectPath = useAppSelector(selectProjectPath);
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);

  const [isValidating, setIsValidating] = useState(false);
  const [isValidationSuccessful, setIsValidationSuccessful] = useState<boolean | undefined>();
  const [skipValidation, setSkipValidation] = useState(false);

  const [validationResults, setValidationResults] = useState<Array<{ feature: string; success: boolean }>>([]);

  const [validateSnowflakeDbtPermissions] = useValidateSnowflakeDbtPermissionsMutation();

  const handleValidate = (): void => {
    setIsValidating(true);
    setIsValidationSuccessful(undefined);

    validateSnowflakeDbtPermissions({
      projectName: projectPath,
      database: 'DATAOPS_DBT_REQUIREMENT_VALIDATION',
      account: snowflakeAccount,
    })
      .unwrap()
      .then((response: Array<{ feature: string; success: boolean }>) => {
        // Check if all features have success: true
        const allSuccessful = response.every((item: { feature: string; success: boolean }) => item.success);
        setValidationResults(response);

        if (allSuccessful) {
          dispatch(setIsSnowflakeAccountValidatedForDbt(true));
          setIsValidationSuccessful(true);
        } else {
          dispatch(setIsSnowflakeAccountValidatedForDbt(false));
          setIsValidationSuccessful(false);
        }
      })
      .catch((error) => {
        console.error('Error validating Snowflake account:', error);
        dispatch(setIsSnowflakeAccountValidatedForDbt(false));
        setIsValidationSuccessful(false);
      })
      .finally(() => {
        setIsValidating(false);
      });
  };

  return (
    <StepWrapper
      title="Execution mode"
      subtitle="Select the dbt™ execution mode"
      onBack={() => props.onBack()}
      onContinue={() => props.onContinue()}
      continueDisabled={dbtExecutionMode === 'snowflake' && !isSnowflakeAccountValidatedForDbt && !skipValidation}
    >
      <div className="relative">
        <fieldset className="mt-4 w-3/4 max-w-[40rem] mx-[auto]">
          <div className="divide-y divide-gray-200">
            <div className="relative flex items-start px-6 py-4">
              <div className="min-w-0 flex-1 text-sm leading-6">
                <label htmlFor="snowflake-option" className="font-medium text-gray-900">
                  DataOps.live Runner
                </label>
                <p className="text-gray-500">
                  This option runs all dbt™ operations (compile, run, test etc) from your DataOps.live Runner which then
                  connects to Snowflake to run the actual queries.
                </p>
              </div>
              <div className="ml-3 pr-1 flex h-6 items-center">
                <input
                  id="dataops-option"
                  name="dataops-option"
                  type="radio"
                  checked={dbtExecutionMode === 'dataops_live'}
                  onClick={() => {
                    dispatch(setDbtExecutionMode('dataops_live'));
                  }}
                  className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                />
              </div>
            </div>
            <div className="relative flex items-start px-6 py-4">
              <div className="min-w-0 flex-1 text-sm leading-6">
                <label htmlFor="ddl-option" className="font-medium text-gray-900">
                  Snowflake dbt™ projects (Private Preview)
                </label>
                <p className="text-gray-500">
                  This option uses the new Snowflake dbt™ projects functionality to runs all dbt™ operations (compile,
                  run, test etc) within Snowflake, as well as Snowflake running the queries themselves.
                </p>
                <div className="w-full mt-2 mb-2">
                  <div className="rounded-md bg-blue-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-blue-800">Snowflake account validation</h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p className="mb-2">
                            You must have dbt™ projects enabled in your Snowflake account for this option to work.
                            Please click the button below to validate:
                          </p>
                          <div className="flex flex-col space-y-3">
                            <div className="flex">
                              <button
                                type="button"
                                className={`min-w-[7rem] rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                                  isValidationSuccessful !== undefined
                                    ? 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                    : 'bg-dataops-primary text-white hover:bg-dataops-primary-hover'
                                } disabled:bg-gray-400`}
                                onClick={() => {
                                  handleValidate();
                                  dispatch(setDbtExecutionMode('snowflake'));
                                }}
                                disabled={isValidating}
                              >
                                Validate Snowflake account
                              </button>
                              {isValidating && (
                                <div className="ml-3 flex items-center">
                                  <div
                                    className="animate-spin inline-block w-5 h-5 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
                                    role="status"
                                    aria-label="loading"
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center">
                              <input
                                id="skip-validation"
                                name="skip-validation"
                                type="checkbox"
                                checked={skipValidation}
                                onChange={(e) => {
                                  setSkipValidation(e.target.checked);
                                  dispatch(setDbtExecutionMode('snowflake'));
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-dataops-primary focus:ring-dataops-primary"
                              />
                              <label htmlFor="skip-validation" className="ml-2 text-sm text-gray-500">
                                Skip validation (not recommended)
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {isValidationSuccessful === false && (
                  <Disclosure as="div" defaultOpen={false} className="w-full rounded-md bg-red-50 overflow-hidden">
                    {({ open }) => (
                      <>
                        <DisclosureButton className="w-full p-4 flex">
                          <div className="shrink-0">
                            <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-red-600" />
                          </div>
                          <div className="ml-3 grow text-left">
                            <p className="text-sm font-medium text-red-600">
                              dbt™ projects are not enabled in your Snowflake account
                            </p>
                          </div>
                          <ChevronRightIcon
                            className={`h-5 w-5 text-red-500 transition-transform duration-200 ${
                              open ? 'rotate-90' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </DisclosureButton>
                        <DisclosurePanel
                          transition
                          className="px-4 pb-4 origin-top transition duration-200 ease-out data-closed:-translate-y-6 data-closed:opacity-0"
                        >
                          <div className="ml-8 flex-1 space-y-2">
                            <p className="text-sm text-red-600 mb-3">
                              The following requirements for dbt™ projects are not met in your Snowflake account:
                            </p>
                            <ValidationResultsTable results={validationResults} />
                            <p className="text-sm text-red-600 mt-3">
                              Please contact your Snowflake SE to discuss enabling these features.
                            </p>
                          </div>
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                )}
                {isValidationSuccessful === true && (
                  <Disclosure as="div" defaultOpen={false} className="w-full rounded-md bg-green-50 overflow-hidden">
                    {({ open }) => (
                      <>
                        <DisclosureButton className="w-full p-4 flex">
                          <div className="shrink-0">
                            <CheckCircleIcon aria-hidden="true" className="size-5 text-green-600" />
                          </div>
                          <div className="ml-3 grow text-left">
                            <p className="text-sm font-medium text-green-600">
                              dbt™ projects are enabled in your Snowflake account
                            </p>
                          </div>
                          <ChevronRightIcon
                            className={`h-5 w-5 text-green-500 transition-transform duration-200 ${
                              open ? 'rotate-90' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </DisclosureButton>
                        <DisclosurePanel
                          transition
                          className="px-4 pb-4 origin-top transition duration-200 ease-out data-closed:-translate-y-6 data-closed:opacity-0"
                        >
                          <div className="ml-8 flex-1 space-y-2">
                            <p className="text-sm text-green-600 mb-3">
                              Your Snowflake account has all the necessary permissions to use dbt™ projects:
                            </p>
                            <ValidationResultsTable results={validationResults} />
                            <p className="text-sm text-green-600 mt-3">
                              You can proceed with the Snowflake execution mode.
                            </p>
                          </div>
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                )}
                {skipValidation && isValidationSuccessful === undefined && (
                  <div className="w-full rounded-md bg-yellow-50 p-4">
                    <div className="flex">
                      <div className="shrink-0">
                        <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-600" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-yellow-800">
                          Proceeding without validation may impact pipeline execution if the Snowflake dbt™ projects
                          feature is not enabled in your account. Validation is recommended to ensure compatibility.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="ml-3 pr-1 flex h-6 items-center">
                <input
                  id="snowflake-option"
                  name="snowflake-option"
                  type="radio"
                  checked={dbtExecutionMode === 'snowflake'}
                  onClick={() => {
                    dispatch(setDbtExecutionMode('snowflake'));
                  }}
                  className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </StepWrapper>
  );
}

// Shared component for validation results table
interface ValidationResultsTableProps {
  results: Array<{ feature: string; success: boolean }>;
}

function ValidationResultsTable({ results }: ValidationResultsTableProps): JSX.Element {
  return (
    <div className="overflow-hidden rounded-md border border-gray-300">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-white">
          <tr>
            <th scope="col" className="py-2 pl-4 pr-3 text-left text-xs text-gray-900 font-medium">
              Feature
            </th>
            <th scope="col" className="px-3 py-2 text-center text-xs text-gray-900 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {results.map((result, index) => (
            <tr key={index}>
              <td className="whitespace-normal py-2 pl-4 pr-3 text-xs text-gray-900">{result.feature}</td>
              <td className="whitespace-nowrap px-3 py-2 text-center text-xs">
                {result.success ? (
                  <CheckCircleIcon className="inline-block h-4 w-4 text-green-500" aria-hidden="true" />
                ) : (
                  <ExclamationTriangleIcon className="inline-block h-4 w-4 text-red-500" aria-hidden="true" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
